import { parseJsonSafely } from '../helpers.js';
import { formatRetryAfter, normalizeDownloadUrl } from './helpers.js';

export async function submitAccountExport(fetchImpl, { csrfToken, exportUrl }) {
    const response = await fetchImpl(exportUrl, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
        },
        body: '{}',
    });

    const payload = await parseJsonSafely(response);
    const downloadUrl = normalizeDownloadUrl(payload.download_url);

    if (response.ok && payload.success === true && downloadUrl !== null) {
        return {
            kind: 'ready',
            downloadUrl,
            expiresAt: typeof payload.expires_at === 'string' ? payload.expires_at : null,
        };
    }

    if (response.status === 429) {
        const retryAfterSeconds = readRetryAfterSeconds(response, payload);
        const retryAfter = formatRetryAfter(retryAfterSeconds);

        if (retryAfterSeconds === null) {
            return {
                kind: 'failed',
                message: typeof payload.error === 'string' && payload.error.trim() !== ''
                    ? payload.error.trim()
                    : 'Too many export requests. Please try again later.',
                tone: 'warning',
            };
        }

        return {
            kind: 'rate-limited',
            retryAfterSeconds,
            message: retryAfter === null
                ? 'Too many export requests. Please try again later.'
                : `Too many export requests. Try again in about ${retryAfter}.`,
        };
    }

    const errorMessage = typeof payload.error === 'string' && payload.error.trim() !== ''
        ? payload.error.trim()
        : 'Export is temporarily unavailable. Please try again later.';

    return {
        kind: 'failed',
        message: errorMessage,
        tone: response.status >= 500 ? 'error' : 'warning',
    };
}

function readRetryAfterSeconds(response, payload) {
    const payloadRetryAfterSeconds = normalizeRetryAfterSeconds(payload.retry_after_seconds);
    if (payloadRetryAfterSeconds !== null) {
        return payloadRetryAfterSeconds;
    }

    const legacyPayloadRetryAfterSeconds = normalizeRetryAfterSeconds(payload.retry_after);
    if (legacyPayloadRetryAfterSeconds !== null) {
        return legacyPayloadRetryAfterSeconds;
    }

    return normalizeRetryAfterSeconds(readRetryAfterHeader(response));
}

function readRetryAfterHeader(response) {
    const headers = response?.headers;

    if (headers === null || headers === undefined) {
        return null;
    }

    if (typeof headers.get === 'function') {
        return headers.get('Retry-After');
    }

    if (typeof headers === 'object') {
        return headers['Retry-After'] ?? headers['retry-after'] ?? null;
    }

    return null;
}

function normalizeRetryAfterSeconds(value) {
    const numericValue = typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : value;

    if (Number.isFinite(numericValue) && numericValue > 0) {
        return Math.ceil(numericValue);
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }

    const retryAtTimestamp = Date.parse(value);
    if (!Number.isFinite(retryAtTimestamp)) {
        return null;
    }

    const retryAfterSeconds = Math.ceil((retryAtTimestamp - Date.now()) / 1000);

    return retryAfterSeconds > 0 ? retryAfterSeconds : null;
}
